import _ from "underscore";
import DriveView from "../../DriveView";
import { Device } from "../../model/Device";
import ManageTrailersView from "../assets/manageTrailers";
import ManageShipmentsView from "./manageShipments";
import { getCurrentVehicle, isNoVehicle } from "../../utils/device";
import { BaseCache, makeDeviceCache } from "../../utils/storage/cache";
import { hide, show, hashToArray } from "../../utils/vanilla";
import { PageLevel } from "../../store/mobile/types";
import { isIoxUsbDeviceAttached } from "../../services/ioxDataHandler";
import ButtonsBar from "../framework/buttonsBar";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { SyncType } from "../../sync/sync.types";
import Spinner from "../../utils/spinner";
import DetachAllPanelView from "./detachAllHelper";
import constants from "../../constants";
import { Dialog } from "../framework/dialog";
import { getAttachmentsFromState, setAttachmentsInState, clearTempAssetsFromState, getShipmentsFromState } from "./helper";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { IShipment, Shipment } from "../../model/Shipment";
import { handleApi } from "../../utils/error";

import html from "./main.tmpl";
import { getShipments } from "../../model/Shipments";

export default class AssetsView extends DriveView<Backbone.Model> {

    protected strings = [
        "Save and continue",
        "Synchronizing your data...",
        "Cannot attach more than {0} trailers. Please detach existing trailers in order to add new assets.",
        "Are you sure you want to detach {0} trailers?"
    ];
    private readonly template = _.template(html);
    private readonly manageTrailersView?: ManageTrailersView;
    private readonly manageShipmentsView?: ManageShipmentsView;
    private readonly workflow: boolean;
    private readonly logout: boolean;
    private readonly detachAllView: DetachAllPanelView;
    private readonly deviceCache: BaseCache<IDevice | Device>;
    private buttonsBar: ButtonsBar | undefined;
    private assetSpinner: Spinner | null;

    constructor(options) {
        super(options, {
            title: "Assets",
            activeSection: "assets",
            pageLevel: options.pageLevel ?? PageLevel.SUBMENU_WITH_BACK_BUTTON
        });
        this.workflow = options.workflow || false;
        this.logout = options.logout || false;
        if (!this.workflow || this.logout) {
            clearTempAssetsFromState(this.app.store, this.app.userService);
        }
        this.deviceCache = makeDeviceCache(this.app.store);

        this.assetSpinner = new Spinner();

        if (!isNoVehicle()) {
            if (this.app.securityService.can("viewTrailers")) {
                this.manageTrailersView = new ManageTrailersView({ app: this.app, workflow: this.workflow });
                this.listenTo(this.manageTrailersView, "trailerRemoved", () => {
                    this.shouldBlockAddingTrailers();
                    this.areAllAssetsDetached();
                });

                if (this.workflow) {
                    this.detachAllView = new DetachAllPanelView({
                        getConfirmMessage: () => this.getConfirmMessage(),
                        getDetachErrorMessage: () => this.getDetachErrorMessage(),
                        detachCallback: () => this.detachAll(),
                        detachButtonText: "Detach all trailers",
                        app: this.app
                    });
                }
            }

            if (this.app.securityService.can("viewShipments")) {
                this.manageShipmentsView = new ManageShipmentsView({ app: this.app, workflow: this.workflow });
                this.listenTo(this.manageShipmentsView, "shipmentRemoved", () => this.areAllAssetsDetached());
            }
        }

        this.storeUnsubscribe = this.app.observeStore(state => state.mobile.ioxAttached, () => this.renderImpl());
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #chooseVehicle": this.chooseVehicle,
            "click .panel__title-button": this.navigate
        } as Backbone.EventsHash;
    }
    /* eslint-disable @typescript-eslint/unbound-method */

    render() {
        this.createContinueButton();
        this.renderImpl();
        return this;
    }

    remove() {
        this.buttonsBar && this.buttonsBar.hide();
        delete this.buttonsBar;

        this.detachAllView?.remove();
        this.manageTrailersView?.remove();
        this.manageShipmentsView?.remove();

        if (this.assetSpinner) {
            this.assetSpinner.stop();
            this.assetSpinner = null;
        }

        super.remove();
        return this;
    }

    private renderImpl() {
        this.toggleContinueButton(false);
        let currentVehicleId = getCurrentVehicle();
        let vehicle = new Device(this.deviceCache.get(currentVehicleId ? { id: currentVehicleId.id } : {})[0]);

        this.display(this.template({
            vehicle: vehicle,
            hasVehicle: !isNoVehicle(),
            canAttachTrailers: !!this.manageTrailersView,
            canAddShipments: !!this.manageShipmentsView,
            logout: this.logout
        })).then(() => {
            if (this.manageTrailersView) {
                let trailersListElement = document.getElementById("manageTrailersList");
                trailersListElement && this.manageTrailersView.setElement(trailersListElement).render();
            }

            if (this.manageShipmentsView) {
                let shipmentListElement = document.getElementById("manageShipmentsList");
                shipmentListElement && this.manageShipmentsView.setElement(shipmentListElement).render();
            }

            this.shouldBlockAddingTrailers();

            let isIoxAttached = isIoxUsbDeviceAttached(this.app.store);
            let chooseAssetButton = document.getElementById("chooseVehicle") as HTMLButtonElement;
            if (chooseAssetButton) {
                isIoxAttached ? hide(chooseAssetButton) : show(chooseAssetButton);
            }
            let ioxChangeVehicleMsg = document.getElementById("messageDisableChooseVehicleButton") as HTMLElement;
            if (ioxChangeVehicleMsg) {
                isIoxAttached ? show(ioxChangeVehicleMsg) : hide(ioxChangeVehicleMsg);
            }

            this.toggleContinueButton(true);
        }).catch(handleApi);
    }

    private createContinueButton() {
        if (!this.workflow && !this.options?.state?.next) {
            return;
        }

        this.buttonsBar && this.buttonsBar.hide();
        this.buttonsBar = new ButtonsBar([{
            title: this.getString("Save and continue"),
            id: "driver-asset-selection-last-button",
            clickHandler: () => this.continue()
        }], {
            isNightMode: this.app.store.getState().data.nightMode
        });

        this.buttonsBar.show();
    }

    private syncTempAttachments() {
        let tempAttachments = getAttachmentsFromState(this.app.store, this.app.userService);
        let currentVehicle = getCurrentVehicle() as IIdEntity;

        if (!currentVehicle || isNoVehicle() || tempAttachments.length === 0) {
            return Promise.resolve();
        }
        return Promise.all(tempAttachments.map(tempAttachment => {
            tempAttachment.device = currentVehicle;
            delete tempAttachment._isTemp;
            return this.app.sync.trailerAttachments.addItem(tempAttachment, true);
        }));
    }

    private syncTempShipments() {
        let tempShipments = getShipmentsFromState(this.app.store, this.app.userService);
        let currentVehicle = getCurrentVehicle() as IIdEntity;

        if (!currentVehicle || isNoVehicle() || tempShipments.length === 0) {
            return Promise.resolve();
        }
        return Promise.all(tempShipments.map(tempShipment => {
            let modifiedShipment = {...tempShipment};
            delete modifiedShipment._isTemp;
            modifiedShipment.device = currentVehicle;
            return this.app.sync.shipments.addItem(new Shipment(modifiedShipment), true);
        }));
    }

    private continue() {
        if (this.logout) {
            dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep);
            return;
        }

        if (this.shouldBlockAddingTrailers()) {
            let assetDetachTrailers = document.querySelector("#select-asset-detach-trailers") as HTMLElement;
            assetDetachTrailers.scrollIntoView();
            return;
        }

        this.startProgress();
        this.syncTempAttachments()
            .then(() => this.syncTempShipments())
            .then(() => {
                clearTempAssetsFromState(this.app.store, this.app.userService);
                let apiUser = this.app.api.getUser();
                let selectiveSync: SyncType[] = [];

                let trailerAttachments = hashToArray(apiUser ? (this.app.store.getState().trailerAttachments[apiUser.id] || {}) : {});
                trailerAttachments.some(trailerAttachment => !!trailerAttachment.modified) && selectiveSync.push(SyncType.TrailerAttachments);
                const shipmentsDict = getShipments(this.app.store);
                let shipments: IShipment[] = hashToArray(shipmentsDict);
                shipments.some(shipment => !!shipment.modified) && selectiveSync.push(SyncType.Shipments);

                if (selectiveSync.length > 0) {
                    return this.app.sync.synchronize(false, null, selectiveSync);
                }
                return Promise.resolve();
            })
            .then(() => {
                let nextPage = this.options?.state?.next;
                if (nextPage) {
                    this.app.router.navigate(nextPage, true);
                } else {
                    dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep);
                }
            })
            .catch(handleApi);
    }

    private navigate(e: Event) {
        let target = e && (e.currentTarget as HTMLLIElement);
        let dataLink = target && target.getAttribute("data-id");
        if (dataLink) {
            if (this.workflow) {
                dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep, dataLink);
            } else {
                this.app.router.navigate(`assets/${ dataLink }`, true);
            }
        }
    }

    private toggleContinueButton(enable: boolean) {
        if (this.buttonsBar) {
            if (enable) {
                this.buttonsBar.enableButtons();
            } else {
                this.buttonsBar.disableButtons();
            }
        }
    }

    private startProgress() {
        this.toggleContinueButton(false);

        let assetsPanel = document.getElementById("manage-assets");
        assetsPanel && hide(assetsPanel);

        let selectionProgress = document.getElementById("selection-waiting");
        selectionProgress && show(selectionProgress);

        let searchTextElement = document.getElementById("driver-asset-selection-search-text");
        searchTextElement && (searchTextElement.innerHTML = this.getString("Synchronizing your data..."));

        let searchSpinner = document.getElementById("driver-asset-selection-search-spinner");
        searchSpinner && this.assetSpinner?.spin(searchSpinner);
    }

    private moveTrailersToNewVehicle() {
        let tempAttachments = getAttachmentsFromState(this.app.store, this.app.userService);
        let redirectCallback = () => dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep, "selectVehicle");
        if (tempAttachments.length > 0) {
            let reassignTrailersWarning = new Dialog(
                "reassign-trailers-confirm",
                "Would you like to move the trailers you just attached to your new vehicle, or detach them?",
                this.translator,
                "Confirmation Required",
                [
                    [{
                        text: "Detach all",
                        className: "button_secondary",
                        id: "reassign-trailers-cancel",
                        callback: () => {
                            setAttachmentsInState(this.app.store, [], this.app.userService);
                            reassignTrailersWarning.remove();
                            redirectCallback();
                        }
                    }, {
                        text: "Move to new vehicle",
                        id: "reassign-trailers-accept",
                        callback: () => {
                            reassignTrailersWarning.remove();
                            redirectCallback();
                        }
                    }]
                ],
                {
                    isNightMode: this.app.store.getState().data.nightMode
                }
            );
            reassignTrailersWarning.open().catch(handleApi);
        } else {
            redirectCallback();
        }
    }

    private chooseVehicle() {
        if (this.workflow) {
            this.moveTrailersToNewVehicle();
        } else {
            this.app.router.toWorkflow(false, { changeVehicle: true });
        }
    }

    private getActiveTrailers() {
        return this.manageTrailersView?.getCurrentTrailerAttachments() ?? [];
    }

    private getConfirmMessage() {
        return this.getString("Are you sure you want to detach {0} trailers?")
            .replace("{0}", this.getActiveTrailers().length.toString());
    }

    private getDetachErrorMessage() {
        return this.getString("Cannot attach more than {0} trailers. Please detach existing trailers in order to add new assets.")
            .replace("{0}", constants.ASSET_LIMITS.MAX_TRAILERS_LIMIT.toString());
    }

    private detachAll() {
        let trailerAttachments = this.getActiveTrailers();
        return Promise.all(trailerAttachments.map(trailerAttachment => {
            trailerAttachment.detach();
            if (!trailerAttachment._isTemp) {
                return this.app.sync.trailerAttachments.setItem(trailerAttachment, true);
            }
            return Promise.resolve();
        })).then(() => {
            setAttachmentsInState(this.app.store, [], this.app.userService);
            this.manageTrailersView?.refresh();
            this.shouldBlockAddingTrailers();
        });
    }

    private shouldBlockAddingTrailers() {
        if (!this.detachAllView) {
            return false;
        }

        let assetDetachTrailers = document.querySelector("#select-asset-detach-trailers") as HTMLElement;
        let activeTrailerCount = this.getActiveTrailers().length;
        if (activeTrailerCount > constants.ASSET_LIMITS.MAX_TRAILERS_LIMIT) {
            if (assetDetachTrailers && this.detachAllView) {
                this.detachAllView.setElement(assetDetachTrailers);
                this.detachAllView.show(true);
                show(assetDetachTrailers);
            }
            return true;
        } else {
            assetDetachTrailers && hide(assetDetachTrailers);
            return false;
        }
    }

    private areAllAssetsDetached() {
        if (!this.logout) {
            return;
        }

        let shipmentCount = this.manageShipmentsView?.getCurrentShipments().length ?? 0;
        let trailersCount = this.manageTrailersView?.getCurrentTrailerAttachments().length ?? 0;
        if (shipmentCount === 0 && trailersCount === 0) {
            this.continue();
        }
    }
}
